import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Button, ChoiceCard, ChoiceGroup, CodeField, Combobox, DataTable, DefinitionList, Dialog, EmptyState, ErrorSummary,
  FormSection, Inline, Link, LiveSummary, Select, Spinner, Stack, t, Text, TextField, toast, WizardLayout,
} from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { RefusalPage } from '../components/Refusal';
import { QualificationDraft, QualificationFields } from '../components/catalogue/QualificationFields';
import { PendingButton } from '../components/PendingButton';
import { openPullRequestOf, PullRequestConflict } from '../components/PullRequestConflict';
import { api, ApiFailure, causeOf, unwrap } from '../lib/api';
import { DeploymentExtras, deploymentsOf, DraftApplication, guessedEnvironmentType, onlyGroup, prefill, Unit } from '../lib/declaration';
import { useSession } from '../lib/session';
import { asName, isEnvironmentName, useEnvironmentName } from '../lib/environments';
import { FULL_HEIGHT } from '../lib/tables';
import { changeRef } from '../components/catalogue/PullRequestNotice';

type PullRequest = maintenance.components['schemas']['PullRequest'];
type Deployment = ReturnType<typeof deploymentsOf>[number];

const STEPS = ['project', 'applications', 'containers', 'deployments', 'verification'] as const;
const NEW = '__new__';
/** D48: an environment of the project, or a new one, a free name and a type. */
type Environment = { name: string; type: string | null };
const PR_TONES = { open: 'info', validation_failed: 'neg', merged: 'info', applied: 'pos', closed: 'neutral' } as const;
const EMPTY_QUALIFICATION: QualificationDraft = { criticality: null, healthData: null, dora: null, sensitivePersonalData: null, internetExposed: null };

/** Where a backend error is shown: the step that holds the field. */
function stepOf(e: { step: string; field: string }): number {
  if (e.step === 'client' || e.step === 'project') return 0;
  if (e.step === 'applications') return 1;
  return e.field === 'deployments' || e.field.endsWith('.containers') ? 2 : 3;
}

const ruleText = (d: Deployment) => [
  ...(d.containers ?? []).map(r => (r.kind === 'compose' ? `compose:${r.project}${r.services ? `/${r.services.join(',')}` : ''}` : `prefix:${r.prefix}`)),
  ...(d.units ?? []).map(r => `${r.kind}:${r.prefix}`),
].join(' ; ');

/**
 * D48 « Déclarer une application », DSM Wizard page type, from a server's
 * page: Client et projet, Applications, Conteneurs, Déploiements, Vérification,
 * then the pull request opened. Everything is prefilled from what the agent
 * reads and every line can be corrected; the backend checks it all.
 */
export function DeclareApplicationPage() {
  const envName = useEnvironmentName();
  const { server = '' } = useParams();
  // D48: « Déclarer » on one detected application of the server: its lines only.
  const group = useSearchParams()[0].get('group');
  const navigate = useNavigate();
  const { enums } = useSession();
  const [step, setStep] = useState(0);
  const [tried, setTried] = useState<Record<number, boolean>>({});
  const [client, setClient] = useState<string | null>(null);
  const [newClient, setNewClient] = useState({ title: '', contactEmail: '' });
  const [project, setProject] = useState<string | null>(null);
  const [newProject, setNewProject] = useState({ title: '', team: null as string | null });
  const [qualification, setQualification] = useState<QualificationDraft>(EMPTY_QUALIFICATION);
  const [applications, setApplications] = useState<DraftApplication[]>([]);
  const [units, setUnits] = useState<Unit[] | null>(null);
  const [extras, setExtras] = useState<Record<string, DeploymentExtras>>({});
  const [quit, setQuit] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [sending, setSending] = useState(false);
  const [opened, setOpened] = useState<PullRequest | null>(null);
  // D48: the environments the person adds (name and type), and the line that asked for one.
  const [newEnvironments, setNewEnvironments] = useState<Environment[]>([]);
  const [adding, setAdding] = useState<{ unit: string; name: string; type: string | null } | null>(null);

  const options = useQuery({
    queryKey: ['declare-options', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/servers/{server}/applications/options', { params: { path: { server } } })),
    retry: false,
  });
  const running = useQuery({
    queryKey: ['server-running', server],
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/running', { params: { path: { server } } })),
  });
  // The first guess, once: then the person's corrections are kept.
  useEffect(() => {
    if (units || !running.data || !options.data) return;
    const guess = onlyGroup(prefill(running.data.containers, running.data.sites, options.data.organisation, running.data.units ?? [], running.data.ignored.flatMap(i => i.matches)), group);
    setUnits(guess.units);
    setApplications(guess.applications);
  }, [running.data, options.data, units, group]);

  // D48: the project's environments keep their type; a guessed or added one brings its own.
  const environments = useMemo(() => {
    const known = (options.data?.projects ?? []).find(p => p.name === project)?.deployments ?? [];
    const list = new Map<string, Environment>();
    for (const d of known) list.set(d.environment, { name: d.environment, type: d.environmentType });
    for (const e of newEnvironments) if (!list.has(e.name)) list.set(e.name, e);
    for (const u of units ?? []) if (u.environment && !list.has(u.environment)) list.set(u.environment, { name: u.environment, type: guessedEnvironmentType(u.environment) });
    return [...list.values()];
  }, [options.data, project, newEnvironments, units]);
  const deployments = useMemo(
    () => deploymentsOf(units ?? [], extras, env => environments.find(e => e.name === env)?.type ?? null),
    [units, extras, environments],
  );
  const body = useMemo(() => ({
    client: client === NEW ? { new: newClient } : client ? { name: client } : undefined,
    project: project === NEW
      ? { new: { title: newProject.title, team: newProject.team ?? undefined, criticality: qualification.criticality ?? undefined, qualification: { healthData: qualification.healthData, dora: qualification.dora, sensitivePersonalData: qualification.sensitivePersonalData, internetExposed: qualification.internetExposed } } }
      : project ? { name: project } : undefined,
    // An application no line takes is not declared, once the lines are sorted (« Ce qui tourne »):
    // until then, every application named counts (the environments are set at that step).
    applications: step <= STEPS.indexOf('applications')
      ? applications.filter(a => a.name)
      : applications.filter(a => deployments.some(d => d.application === a.name)),
    deployments,
  }), [client, newClient, project, newProject, qualification, applications, deployments, step]);
  const preview = useQuery({
    queryKey: ['declare-preview', server, body],
    queryFn: async () => unwrap(await api.maintenance.POST('/servers/{server}/applications/preview', { params: { path: { server } }, body })),
    placeholderData: keepPreviousData,
    enabled: options.isSuccess,
  });
  const state = useQuery({
    queryKey: ['catalogue-change', opened?.id],
    queryFn: async () => unwrap(await api.maintenance.GET('/catalogue-changes/{id}', { params: { path: { id: opened!.id } } })),
    enabled: Boolean(opened),
    refetchInterval: q => (q.state.data && ['applied', 'closed'].includes(q.state.data.state) ? false : 5000),
  });

  // « Continuer » clicked while the draft is being checked: kept, and applied once the check is back without errors.
  const [advancing, setAdvancing] = useState(false);
  // Before any early return: the hooks of a component are the same at every render.
  useEffect(() => {
    if (!advancing || preview.isFetching) return;
    setAdvancing(false);
    if (!(preview.data?.errors ?? []).some(e => stepOf(e) === step)) setStep(step + 1);
  }, [advancing, preview.isFetching]);
  if (options.error) return <RefusalPage error={options.error} />;
  const data = options.data;
  const back = () => navigate(`/servers/${encodeURIComponent(server)}?tab=applications`);
  const errors = preview.data?.errors ?? [];
  const errorsOf = (s: number) => errors.filter(e => stepOf(e) === s);
  const fieldError = (field: string) => (tried[step] ? errors.find(e => e.field === field)?.message : undefined);
  const steps = STEPS.map(s => t(`declare.steps.${s}`));
  const projects = (data?.projects ?? []).filter(p => p.client === client);
  const chosen = projects.find(p => p.name === project);
  const clientTitle = client === NEW ? newClient.title : data?.clients.find(c => c.name === client)?.title;
  const projectTitle = project === NEW ? newProject.title : chosen?.title;
  const typeLabel = (type: string | null) => enums.environmentTypes.find(x => x.value === type)?.label ?? t('declare.environments.typeTodo');
  const environmentOptions = [
    ...environments.map(e => ({ value: e.name, label: envName(e.name), meta: typeLabel(e.type) })),
    { value: NEW, label: t('declare.environments.new') },
  ];
  const roleLabel = (role: string) => enums.applicationRoles.find(r => r.value === role)?.label ?? role;
  const appOptions = applications.filter(a => a.name).map(a => ({ value: a.name, label: a.name }));
  const filled = Boolean(client || project);
  const close = () => (filled && !opened ? setQuit(true) : back());

  const next = () => {
    setTried(x => ({ ...x, [step]: true }));
    if (preview.isFetching) { setAdvancing(true); return; }
    if (errorsOf(step).length) return;
    setStep(step + 1);
  };
  const submit = async () => {
    setSending(true);
    setFailure(null);
    try {
      const result = unwrap(await api.maintenance.POST('/servers/{server}/applications', { params: { path: { server } }, body }));
      setOpened(result.pullRequest);
      toast({ tone: 'pos', title: t('declare.done.toast') });
    } catch (e) {
      setFailure(e instanceof ApiFailure ? e : null);
    } finally {
      setSending(false);
    }
  };
  const renameApplication = (i: number, name: string) => {
    const before = applications[i]!.name;
    const known = chosen?.applications.find(a => a.name === name);
    setApplications(list => list.map((a, k) => (k === i ? { name, role: known?.role ?? a.role, repository: known ? known.repository : a.repository } : a)));
    setUnits(list => (list ?? []).map(u => (u.application === before ? { ...u, application: name } : u)));
  };
  const setUnit = (key: string, patch: Partial<Unit>) => setUnits(list => (list ?? []).map(u => (u.key === key ? { ...u, ...patch } : u)));
  const setExtra = (key: string, d: Deployment, patch: Partial<DeploymentExtras>) =>
    setExtras(x => ({ ...x, [key]: { url: x[key]?.url ?? d.url ?? null, external: x[key]?.external ?? '', alsoUses: x[key]?.alsoUses ?? '', ...patch } }));

  // --- After the last step: the pull request opened (DSM Wizard, « et maintenant ? »).
  if (opened) {
    const live = state.data ?? opened;
    const applied = live.state === 'applied';
    return (
      <WizardLayout
        title={t('declare.title', { server })}
        steps={steps}
        current={4}
        onClose={back}
        closeLabel={t('declare.quit')}
        eyebrow={false}
        heading={t('declare.done.heading')}
        lead={t('declare.done.lead')}
        footerEnd={<Button trailingIcon="arrow_forward" onClick={back}>{t('declare.done.back')}</Button>}
        footerText={applied ? '' : <Inline gap={8} align="center"><Spinner size={16} />{t('declare.done.waiting')}</Inline>}
      >
        <DefinitionList
          items={[
            { label: t('declare.done.pullRequest'), value: <Link href={live.url} external>{t('pullRequest.link', { ref: changeRef(live) })}</Link> },
            { label: t('declare.done.state'), value: <Badge tone={PR_TONES[live.state]}>{t(`newProject.prStates.${live.state}`)}</Badge> },
          ]}
        />
      </WizardLayout>
    );
  }

  const summaryErrors = tried[step] ? errorsOf(step).map(e => ({ message: e.message, fieldId: e.field.replace(/[^a-zA-Z0-9]/g, '-') })) : [];
  const updated = new Set(preview.data?.updated ?? []);

  return (
    <>
      <WizardLayout
        title={t('declare.title', { server })}
        subtitle={t('declare.subtitle')}
        steps={steps}
        current={step}
        onStepSelect={i => { if (i < step) setStep(i); }}
        onClose={close}
        closeLabel={t('declare.quit')}
        heading={t(`declare.${STEPS[step]}.heading`)}
        lead={t(`declare.${STEPS[step]}.lead`, { server })}
        maxWidth={step >= 1 ? 1240 : undefined}
        aside={step === 0 ? (
          <LiveSummary
            eyebrow={t('declare.summary.eyebrow')}
            title={projectTitle || t('declare.summary.titleEmpty')}
            rows={[
              { icon: 'business', value: clientTitle || null, placeholder: t('declare.summary.clientTodo') },
              { icon: 'apps', value: applications.length ? t('declare.summary.applications', { n: applications.length }) : null, placeholder: t('declare.summary.applicationsTodo') },
              { icon: 'deployed_code', value: deployments.length ? t('declare.summary.deployments', { n: deployments.length }) : null, placeholder: t('declare.summary.deploymentsTodo') },
            ]}
            footer={t('declare.summary.footer')}
            footerIcon="merge"
          />
        ) : undefined}
        footerStart={<Button variant="secondary" icon="arrow_back" disabled={step === 0} onClick={() => setStep(step - 1)}>{t('declare.previous')}</Button>}
        footerEnd={step < 4
          ? <Button trailingIcon="arrow_forward" onClick={next}>{t('declare.continue')}</Button>
          : <PendingButton icon="merge" pending={sending} disabled={errors.length > 0 || preview.isFetching} onClick={submit}>{t('declare.open')}</PendingButton>}
      >
        <Stack gap={24}>
          {data?.pullRequest && (
            <Alert tone="info" title={t('declare.pending.title')}>
              <Link href={data.pullRequest.url} external>{t('pullRequest.link', { ref: changeRef(data.pullRequest) })}</Link>
            </Alert>
          )}
          {summaryErrors.length > 0 && <ErrorSummary title={t('declare.errorsTitle')} errors={summaryErrors} />}
          {failure && openPullRequestOf(failure) && <PullRequestConflict failure={failure} onClosed={() => setFailure(null)} />}
          {failure && !openPullRequestOf(failure) && (
            <Alert tone="neg" title={failure.body.message || t('newProject.githubDown')}>
              {failure.body.hint}{causeOf(failure) ? ` ${t('errors.cause', { cause: causeOf(failure)! })}` : ''}
            </Alert>
          )}

          {step === 0 && data && (
            <Stack gap={24}>
              <FormSection layout="stacked" headingLevel={2} title={t('declare.project.client')}>
                <Combobox
                  id="client-name"
                  label={t('newProject.client.search')}
                  searchable
                  placeholder={t('newProject.client.searchPlaceholder')}
                  options={data.clients.map(c => ({ value: c.name, label: c.title }))}
                  value={client && client !== NEW ? client : null}
                  onChange={(v: string | null) => { setClient(v); setProject(null); }}
                  error={fieldError('client.name') ?? fieldError('client')}
                />
                {data.canCreateClient && (
                  <ChoiceGroup label={t('declare.project.orNewClient')}>
                    <ChoiceCard variant="radio" title={t('newProject.client.new')} description={t('newProject.client.newHint')}
                      selected={client === NEW} onClick={() => { setClient(NEW); setProject(NEW); }} />
                  </ChoiceGroup>
                )}
                {client === NEW && (
                  <>
                    <TextField id="client-new-title" label={t('newProject.client.name')} value={newClient.title} required
                      onChange={(e: { target: { value: string } }) => setNewClient(c => ({ ...c, title: e.target.value }))} error={fieldError('client.new.title')} />
                    <TextField id="client-new-contactEmail" type="email" label={t('newProject.client.contact')} value={newClient.contactEmail} required hint={t('newProject.client.contactHint')}
                      onChange={(e: { target: { value: string } }) => setNewClient(c => ({ ...c, contactEmail: e.target.value }))} error={fieldError('client.new.contactEmail')} />
                  </>
                )}
              </FormSection>
              {client && (
                <FormSection layout="stacked" headingLevel={2} title={t('declare.project.project')}>
                  {client !== NEW && (
                    <ChoiceGroup label={t('declare.project.pick')}>
                      {projects.map(p => (
                        <ChoiceCard key={p.name} variant="radio" title={p.title} description={t('declare.project.applications', { n: p.applications.length })}
                          selected={project === p.name} onClick={() => setProject(p.name)} />
                      ))}
                      {data.canCreateProject && (
                        <ChoiceCard variant="radio" title={t('declare.project.new')} description={t('declare.project.newHint')}
                          selected={project === NEW} onClick={() => setProject(NEW)} />
                      )}
                    </ChoiceGroup>
                  )}
                  {fieldError('project.name') && <Alert tone="neg">{fieldError('project.name')}</Alert>}
                  {project === NEW && (
                    <>
                      <TextField id="project-new-title" label={t('newProject.project.name')} value={newProject.title} required
                        onChange={(e: { target: { value: string } }) => setNewProject(p => ({ ...p, title: e.target.value }))} error={fieldError('project.new.title')} />
                      <Select id="project-new-team" label={t('newProject.project.team')} searchable required value={newProject.team} onChange={(v: string | null) => setNewProject(p => ({ ...p, team: v }))}
                        options={data.teams.map(x => ({ value: x, label: x }))} placeholder={t('newProject.project.teamPlaceholder')} error={fieldError('project.new.team')} />
                      {/* Mandatory at the creation (RG-006-1): no default answer. */}
                      <QualificationFields value={qualification} onChange={setQualification} errors={Boolean(tried[0])} />
                    </>
                  )}
                </FormSection>
              )}
            </Stack>
          )}

          {step === 1 && (
            <Stack gap={12}>
              <DataTable
                height={FULL_HEIGHT}
                label={t('declare.applications.table')}
                // A field's error shows under it: the row grows instead of overlapping the next one.
                rowHeight="auto"
                rows={applications.map((a, i) => ({ ...a, i }))}
                rowKey="i"
                toolbar={false}
                columns={[
                  {
                    key: 'name', label: t('declare.applications.name'), width: 300, editable: true, sortable: false,
                    render: (a: DraftApplication & { i: number }) => (
                      <Combobox size="sm" ariaLabel={t('declare.applications.nameOf', { n: a.i + 1 })} value={a.name || null} creatable
                        onCreate={(label: string) => renameApplication(a.i, asName(label, 50, true))}
                        onChange={(v: string | null) => renameApplication(a.i, v ?? '')}
                        options={[...new Set([a.name, ...(chosen?.applications.map(x => x.name) ?? [])].filter(Boolean))].map(n => ({ value: n, label: n }))}
                        error={fieldError(`applications[${a.i}].name`)} />
                    ),
                  },
                  {
                    key: 'role', label: t('declare.applications.role'), width: 220, editable: true, sortable: false,
                    render: (a: DraftApplication & { i: number }) => (
                      <Select size="sm" ariaLabel={t('declare.applications.roleOf', { name: a.name })} value={a.role} options={enums.applicationRoles}
                        onChange={(v: string | null) => setApplications(list => list.map((x, k) => (k === a.i ? { ...x, role: v ?? '' } : x)))}
                        error={fieldError(`applications[${a.i}].role`)} />
                    ),
                  },
                  {
                    key: 'repository', label: t('declare.applications.repository'), grow: true, editable: true, sortable: false,
                    render: (a: DraftApplication & { i: number }) => (
                      <TextField size="sm" ariaLabel={t('declare.applications.repositoryOf', { name: a.name })} value={a.repository ?? ''} placeholder={`${data?.organisation ?? ''}/…`}
                        onChange={(e: { target: { value: string } }) => setApplications(list => list.map((x, k) => (k === a.i ? { ...x, repository: e.target.value || null } : x)))}
                        error={fieldError(`applications[${a.i}].repository`)} />
                    ),
                  },
                  {
                    key: 'remove', label: '', width: 64, sortable: false,
                    render: (a: DraftApplication & { i: number }) => (
                      <Button icon="delete" label={t('declare.applications.remove', { name: a.name })} variant="ghost" size="sm"
                        onClick={() => { setApplications(list => list.filter((_, k) => k !== a.i)); setUnits(list => (list ?? []).map(u => (u.application === a.name ? { ...u, application: null } : u))); }} />
                    ),
                  },
                ]}
                empty={<EmptyState framed={false} icon="apps" title={t('declare.applications.empty')} />}
              />
              <Inline>
                <Button variant="secondary" size="sm" icon="add" onClick={() => setApplications(list => [...list, { name: '', role: 'backend', repository: null }])}>{t('declare.applications.add')}</Button>
              </Inline>
            </Stack>
          )}

          {step === 2 && (
            <DataTable
              height={FULL_HEIGHT}
              label={t('declare.containers.table')}
              rows={units ?? []}
              rowKey="key"
              loading={!units}
              toolbar={false}
              columns={[
                {
                  key: 'key', label: t('declare.containers.container'), width: 260, sortable: false,
                  render: (u: Unit) => <Text variant="code" truncate>{u.key}</Text>,
                },
                {
                  // D58: how many containers the line takes, in its own column (never a badge after the name).
                  key: 'containers', label: t('declare.containers.instances'), width: 130, sortable: false,
                  accessor: (u: Unit) => (u.containers.length > 1 ? t('declare.containers.count', { n: u.containers.length }) : ''),
                  render: (u: Unit) => (u.containers.length > 1 ? <Badge tone="neutral" size="sm">{t('declare.containers.count', { n: u.containers.length })}</Badge> : null),
                },
                {
                  key: 'image', label: t('declare.containers.image'), grow: true, sortable: false,
                  // A container shows its image; a service, timer or cron job its nature and detail (state, last run, schedule).
                  render: (u: Unit) => (u.kind === 'container'
                    ? <Text variant="code" truncate>{u.image}</Text>
                    : (
                      <Inline gap={8} align="center" wrap={false}>
                        <Text variant="body-sm">{t(`running.units.kinds.${u.kind}`)}</Text>
                        {u.image && <Text variant="code" tone="mute" truncate>{u.image}</Text>}
                      </Inline>
                    )),
                },
                {
                  key: 'type', label: t('declare.containers.type'), width: 190, editable: true, sortable: false,
                  render: (u: Unit) => (
                    <Select size="sm" ariaLabel={t('declare.containers.typeOf', { name: u.key })} value={u.type ?? 'auto'}
                      options={[{ value: 'auto', label: t('declare.containers.auto') }, ...['service', 'data', 'task'].map(v => ({ value: v, label: t(`bricks.type.${v}`) }))]}
                      onChange={(v: string | null) => setUnit(u.key, { type: v === 'auto' || !v ? null : (v as Unit['type']) })} />
                  ),
                },
                {
                  key: 'application', label: t('declare.containers.application'), width: 240, editable: true, sortable: false,
                  render: (u: Unit) => (
                    <Select size="sm" ariaLabel={t('declare.containers.applicationOf', { name: u.key })} value={u.application ?? 'none'}
                      options={[{ value: 'none', label: t('declare.containers.ignore') }, ...appOptions]}
                      onChange={(v: string | null) => setUnit(u.key, { application: v === 'none' ? null : v })} />
                  ),
                },
                {
                  key: 'environment', label: t('declare.containers.environment'), width: 180, editable: true, sortable: false,
                  render: (u: Unit) => (
                    <Select size="sm" ariaLabel={t('declare.containers.environmentOf', { name: u.key })} value={u.environment} placeholder={t('declare.containers.environmentTodo')}
                      options={environmentOptions}
                      onChange={(v: string | null) => (v === NEW ? setAdding({ unit: u.key, name: '', type: null }) : setUnit(u.key, { environment: v }))} />
                  ),
                },
              ]}
              empty={<EmptyState framed={false} icon="task_alt" title={t('declare.containers.empty')}>{t('declare.containers.emptyBody')}</EmptyState>}
            />
          )}

          {step === 3 && (
            <DataTable
              height={FULL_HEIGHT}
              label={t('declare.deployments.table')}
              rows={deployments.map((d, i) => ({ ...d, i, key: `${d.application}-${d.environment}` }))}
              rowKey="key"
              toolbar={false}
              rowHeight="auto"
              columns={[
                {
                  key: 'key', label: t('declare.deployments.deployment'), width: 260, sortable: false,
                  render: (d: Deployment & { key: string }) => <Text variant="body-sm" strong truncate>{d.application} · {envName(d.environment ?? '')}</Text>,
                },
                {
                  // D58: « Existe déjà » in its own column, never a badge after the deployment's name.
                  key: 'existing', label: t('declare.deployments.existingColumn'), width: 230, sortable: false,
                  accessor: (d: Deployment & { key: string }) => (updated.has(d.key) ? t('declare.deployments.existing') : ''),
                  render: (d: Deployment & { key: string }) => (updated.has(d.key) ? <Badge tone="neutral" size="sm">{t('declare.deployments.existing')}</Badge> : null),
                },
                // The rule that takes its containers, services and tasks.
                { key: 'rule', label: t('declare.deployments.rule'), width: 240, mono: true, sortable: false, accessor: (d: Deployment) => ruleText(d) },
                {
                  key: 'url', label: t('declare.deployments.url'), width: 240, editable: true, sortable: false,
                  render: (d: Deployment & { key: string; i: number }) => (
                    <TextField size="sm" ariaLabel={t('declare.deployments.urlOf', { name: d.key })} value={extras[d.key]?.url ?? d.url ?? ''} placeholder={t('declare.deployments.urlPlaceholder')}
                      onChange={(e: { target: { value: string } }) => setExtra(d.key, d, { url: e.target.value || null })} error={fieldError(`deployments[${d.i}].url`)} />
                  ),
                },
                {
                  key: 'external', label: t('declare.deployments.external'), grow: true, editable: true, sortable: false,
                  render: (d: Deployment & { key: string; i: number }) => (
                    <TextField size="sm" ariaLabel={t('declare.deployments.externalOf', { name: d.key })} value={extras[d.key]?.external ?? ''} placeholder={t('declare.deployments.externalPlaceholder')}
                      onChange={(e: { target: { value: string } }) => setExtra(d.key, d, { external: e.target.value })} error={fieldError(`deployments[${d.i}].external-bricks`)} />
                  ),
                },
                {
                  key: 'alsoUses', label: t('declare.deployments.alsoUses'), width: 220, editable: true, sortable: false,
                  render: (d: Deployment & { key: string; i: number }) => (
                    <TextField size="sm" ariaLabel={t('declare.deployments.alsoUsesOf', { name: d.key })} value={extras[d.key]?.alsoUses ?? ''} placeholder={t('declare.deployments.alsoUsesPlaceholder')}
                      onChange={(e: { target: { value: string } }) => setExtra(d.key, d, { alsoUses: e.target.value })}
                      error={fieldError(`deployments[${d.i}].alsoUses`) ?? fieldError(`deployments[${d.i}].also-uses`)} />
                  ),
                },
              ]}
              empty={<EmptyState framed={false} icon="deployed_code" title={t('declare.deployments.empty')} />}
            />
          )}

          {step === 4 && preview.data && (
            <Stack gap={24}>
              {qualification.healthData && project === NEW && <Alert tone="neg">{t('qualification.healthAlert')}</Alert>}
              <DefinitionList
                items={[
                  { label: t('declare.verification.client'), value: `${clientTitle ?? ''}${client === NEW ? ` ${t('newProject.verification.newClient')}` : ''}` },
                  { label: t('declare.verification.project'), value: `${projectTitle ?? ''}${project === NEW ? ` ${t('declare.verification.newProject')}` : ''}` },
                  { label: t('declare.verification.applications'), value: body.applications.map(a => `${a.name} (${roleLabel(a.role)})`).join(', ') },
                  { label: t('declare.verification.created'), value: preview.data.created.join(', ') || '—' },
                  { label: t('declare.verification.updated'), value: preview.data.updated.join(', ') || '—' },
                ]}
              />
              {preview.data.files.map(f => (
                <CodeField key={f.path} label={`${f.repository} · ${f.path}`} language="yaml" readOnly lineNumbers value={f.content} rows={8} maxHeight={420} />
              ))}
            </Stack>
          )}
        </Stack>
      </WizardLayout>
      {adding && (
        <Dialog
          open
          size="sm"
          onClose={() => setAdding(null)}
          title={t('declare.environments.newTitle')}
          description={t('declare.environments.newBody')}
          confirmLabel={t('declare.environments.newConfirm')}
          cancelLabel={t('declare.quitDialog.cancel')}
          confirmDisabled={!isEnvironmentName(asName(adding.name, 30, true)) || !adding.type}
          onConfirm={() => {
            const name = asName(adding.name, 30, true);
            setNewEnvironments(list => [...list.filter(e => e.name !== name), { name, type: adding.type }]);
            setUnit(adding.unit, { environment: name });
            setAdding(null);
          }}
        >
          <Stack gap={16}>
            <TextField label={t('declare.environments.name')} required value={adding.name} placeholder={t('newProject.environments.namePlaceholder')}
              hint={t('declare.environments.nameHint')} onChange={(e: { target: { value: string } }) => setAdding({ ...adding, name: asName(e.target.value, 30) })} />
            <Select label={t('declare.environments.type')} required value={adding.type} options={enums.environmentTypes} placeholder={t('declare.environments.typeTodo')}
              onChange={(v: string | null) => setAdding({ ...adding, type: v })} />
          </Stack>
        </Dialog>
      )}
      <Dialog
        open={quit}
        onClose={() => setQuit(false)}
        tone="danger"
        title={t('declare.quitDialog.title')}
        description={t('declare.quitDialog.body')}
        confirmLabel={t('declare.quitDialog.confirm')}
        cancelLabel={t('declare.quitDialog.cancel')}
        onConfirm={back}
      />
    </>
  );
}
